/**
 * The working paper header and conclusion (Summary Results tab): the conclusion badge and banner, who prepared and
 * reviewed the run, the AI confidence, how many samples the reviewer has decided, and the conclusion / key findings /
 * recommendations (the reviewer's text over the AI's). Shared by the tab and the working paper (PDF / Excel).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

const NOT_EFFECTIVE = ['ineffective', 'failed', 'not_effective'];

const execConclusion = (exec: any) => String(exec?.conclusion || exec?.overall_status || '').toLowerCase();

export type HeaderConclusion = 'effective' | 'not_effective' | 'needs_review' | 'review_complete' | 'completed';

const HEADER_LABEL: Record<HeaderConclusion, string> = {
  effective: 'EFFECTIVE', not_effective: 'NOT EFFECTIVE', needs_review: 'NEEDS REVIEW',
  review_complete: 'REVIEW COMPLETE', completed: 'COMPLETED',
};

/** The badge in the header: from the run's conclusion; "needs review" becomes "review complete" once signed off. */
export function headerConclusion(exec: any, run: any): { kind: HeaderConclusion; label: string } | null {
  const c = execConclusion(exec);
  let kind: HeaderConclusion | null = null;
  if (c === 'effective') kind = 'effective';
  else if (NOT_EFFECTIVE.includes(c)) kind = 'not_effective';
  else if (c === 'needs_review') kind = run?.workflow_status === 'signed_off' ? 'review_complete' : 'needs_review';
  else if (run?.workflow_status === 'completed') kind = 'completed';
  return kind ? { kind, label: HEADER_LABEL[kind] } : null;
}

/** The banner of the conclusion section. */
export function conclusionBanner(exec: any): { kind: 'effective' | 'not_effective' | 'inconclusive'; label: string } {
  const c = execConclusion(exec);
  if (c === 'effective') return { kind: 'effective', label: 'CONCLUSION: OPERATING EFFECTIVELY — No exceptions noted' };
  if (NOT_EFFECTIVE.includes(c)) return { kind: 'not_effective', label: 'CONCLUSION: NOT OPERATING EFFECTIVELY — Exceptions noted' };
  return { kind: 'inconclusive', label: 'CONCLUSION: INCONCLUSIVE — Further review required' };
}

const lines = (v: any): string[] => String(v || '').split('\n').map((s) => s.trim()).filter(Boolean);

/** The conclusion text, key findings and recommendations: each the reviewer's when they wrote it, else the AI's. */
export function conclusionTexts(run: any, exec: any) {
  const summary = exec?.executive_summary && typeof exec.executive_summary === 'object' ? exec.executive_summary : {};
  const aiConclusion: string | null = summary.audit_conclusion || summary.executive_summary || null;
  const aiFindings: string[] = Array.isArray(summary.key_findings) ? summary.key_findings : [];
  const aiRecommendations: string[] = Array.isArray(summary.recommendations) ? summary.recommendations : [];
  return {
    aiConclusion,
    aiKeyFindings: aiFindings,
    aiRecommendations,
    aiKeyFindingsText: aiFindings.length ? aiFindings.join('\n') : null,
    aiRecommendationsText: aiRecommendations.length ? aiRecommendations.join('\n') : null,
    conclusion: run?.human_conclusion || aiConclusion || '',
    conclusionByReviewer: !!run?.human_conclusion,
    keyFindings: run?.human_key_findings ? lines(run.human_key_findings) : aiFindings,
    keyFindingsByReviewer: !!run?.human_key_findings,
    recommendations: run?.human_recommendations ? lines(run.human_recommendations) : aiRecommendations,
    recommendationsByReviewer: !!run?.human_recommendations,
  };
}

/** "Prepared by": the preparer named at sign-off, else what the run recorded, else AI-assisted. */
export function preparedBy(run: any, preparerInfo?: string | null): { name: string; aiAssisted: boolean } {
  if (run?.workflow_status === 'signed_off' && run?.preparer_name) return { name: run.preparer_name, aiAssisted: true };
  return { name: preparerInfo || 'AI-Assisted Analysis', aiAssisted: false };
}

/** "Reviewed by": the reviewer and the sign-off date, or null while nobody has signed. */
export function reviewedBy(run: any): { name: string; at: string | null } | null {
  const name = run?.signed_off_by || run?.reviewer_name;
  return name ? { name, at: run?.signed_off_at || null } : null;
}

export function confidencePct(exec: any): number | null {
  return exec?.confidence_score != null ? Math.round(exec.confidence_score * 100) : null;
}

const normRef = (ref: any) => (typeof ref === 'string' ? ref.replace(/^["']+|["']+$/g, '').trim() : ref);

/** Samples with results, and how many of them the reviewer has decided (a final result on any attribute). */
export function reviewProgress(attributes: any[]): { reviewed: number; total: number } {
  const refs = [...new Set(attributes.flatMap((a) => (a.sample_results || []).map((sr: any) => normRef(sr.record_ref))))];
  const reviewed = refs.filter((ref) => attributes.some((a) => (a.sample_results || []).some(
    (sr: any) => normRef(sr.record_ref) === ref && sr.final_result,
  ))).length;
  return { reviewed, total: refs.length };
}
